import React from "react";
import { motion } from "framer-motion";
import * as L from "lucide-react";
import { useContent } from "@/lib/cms";

const industries = [
  {
    Icon: L.Plane, name: "Travel",
    useCases: ["Itinerary sharing", "Booking confirmations", "Real-time flight & PNR updates", "Group package broadcasts"],
    tone: "blue",
  },
  {
    Icon: L.Building2, name: "Real Estate",
    useCases: ["Site visit scheduling", "Brochure & price list sharing", "Lead qualification chatbot", "Post-sale documentation"],
    tone: "green",
  },
  {
    Icon: L.GraduationCap, name: "Education",
    useCases: ["Admission enquiry bot", "Fee reminders & receipts", "Class & exam alerts", "Parent-teacher updates"],
    tone: "blue",
  },
  {
    Icon: L.Stethoscope, name: "Healthcare",
    useCases: ["Appointment booking", "Reports & prescriptions", "Vaccination reminders", "Follow-up campaigns"],
    tone: "green",
  },
  {
    Icon: L.ShoppingBag, name: "Ecommerce",
    useCases: ["Abandoned cart recovery", "Order & shipping updates", "Catalogue sharing", "COD verification"],
    tone: "blue",
  },
  {
    Icon: L.Car, name: "Automobile",
    useCases: ["Test-drive scheduling", "Service reminders", "Insurance renewal alerts", "EMI & loan updates"],
    tone: "green",
  },
  {
    Icon: L.Banknote, name: "Finance",
    useCases: ["KYC document collection", "Loan status updates", "Policy renewals", "Statement & invoice delivery"],
    tone: "blue",
  },
  {
    Icon: L.Factory, name: "Manufacturing",
    useCases: ["Dealer & distributor updates", "Order acknowledgements", "Dispatch alerts", "B2B catalogue sharing"],
    tone: "green",
  },
];

const toneMap = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-100" },
  green: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-100" },
};

export const Industries = () => {
  const { data } = useContent("industries", {});
  const items = data.items && data.items.length
    ? data.items.map((it, i) => ({ ...it, Icon: L[it.icon] || L.Sparkles, useCases: industries[i % industries.length].useCases, tone: i % 2 === 0 ? "blue" : "green" }))
    : industries;
  return (
    <section id="industries" className="py-20 sm:py-28 bg-white overflow-hidden" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">Built for every industry</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            WhatsApp Business API use-cases across industries
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            From customer enquiry to closure — Wappit ships ready-to-use WhatsApp flows tailored to your business.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(({ Icon, name, useCases, tone }, i) => {
            const t = toneMap[tone];
            return (
              <motion.div
                key={name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                data-testid={`industry-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl border border-slate-200/70 bg-white p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${t.bg} ${t.text} ring-4 ${t.ring}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{name}</h3>
                <ul className="mt-3 space-y-1.5">
                  {useCases.map((u) => (
                    <li key={u} className="text-sm text-slate-600 flex items-start gap-2">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-emerald-500 flex-shrink-0" />
                      {u}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
